import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import { useToast } from '../../context/ToastContext';

interface MobileGuardProps {
    children: React.ReactNode;
    appName: string;
}

/**
 * Guard component that blocks access to certain apps on mobile devices.
 * Shows a message and redirects to dashboard.
 */
const MobileGuard: React.FC<MobileGuardProps> = ({ children, appName }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { showToast } = useToast();

    useEffect(() => {
        if (isMobile) {
            showToast(`L'application "${appName}" n'est pas disponible sur mobile. Utilisez un ordinateur.`, 'warning');
            // Redirect after a short delay so user sees the toast
            const timer = setTimeout(() => {
                navigate('/dashboard');
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isMobile, appName, navigate, showToast]);

    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    p: 4,
                    textAlign: 'center',
                    color: '#666'
                }}
            >
                <DesktopWindowsIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Application Desktop uniquement
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    "{appName}" n'est pas disponible sur mobile.
                </Typography>
            </Box>
        );
    }

    return <>{children}</>;
};

export default MobileGuard;
